import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { StreamHLSPlayer } from "../components/CMSPanel";
import { ArrowRight, Broadcast } from "@phosphor-icons/react";

/**
 * Public /live-vision page.
 *
 * Shows a wall of Cloudflare-Stream-hosted "camera feeds" with overlaid
 * bounding boxes + labels + confidence scores, driven by admin-uploaded
 * (video_url, boxes) pairs.
 *
 * Layout: a hero card (active feed, big) + a grid of thumbnails.  Clicking a
 * thumbnail promotes it to hero.  Each feed loops via CF Stream's own player.
 *
 * The DetectionOverlay component absolutely-positions coloured boxes on top
 * of the responsive Stream player.  Box coordinates are 0..1 fractions of the
 * video's rendered box, and each has a timestamp — we consult the currently
 * active HTMLVideoElement's `currentTime` on a rAF loop to decide which boxes
 * to render, giving a "live inference" feel synced to the video.
 */
export default function LiveVision() {
  const [demos, setDemos] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    document.title = "Live Vision · Wisewit AI";
    api.get("/cctv-demos").then(({ data }) => setDemos(data || [])).catch(() => {});
  }, []);

  const active = demos[activeIdx];

  if (!demos.length) {
    return (
      <div className="px-6 md:px-14 py-24" data-testid="live-vision-empty">
        <div className="flex items-center gap-3 text-overline mb-4"><Broadcast size={14} color="#FF4500" /> ◢ Live Vision</div>
        <h1 className="font-display text-4xl md:text-5xl text-white leading-tight tracking-tighter">Detections streaming from every floor we cover.</h1>
        <p className="mt-4 text-[#8E8E93] max-w-2xl">
          This is where our AI systems announce themselves — real videos, real detections, overlaid live.
          Publish your first demo in <Link to="/admin" className="text-[#DFFF00] hover:text-white">Admin → Live Vision</Link> to fill this wall.
        </p>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-14 py-16 md:py-24" data-testid="live-vision-page">
      <div className="flex items-end justify-between gap-6 max-w-6xl">
        <div>
          <div className="flex items-center gap-3 text-overline mb-3">
            <Broadcast size={14} color="#FF4500" weight="fill" /> ◢ Live Vision
            <span className="ww-pulse ml-1" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-white leading-[1.02] tracking-tighter">
            Detections streaming from every floor we cover.
          </h1>
          <p className="mt-4 text-[#8E8E93] max-w-2xl">
            Real footage. Real detections. Bounding boxes rendered in real time synced to each clip's ONNX inference log.
          </p>
        </div>
        <Link to="/contact" data-testid="lv-cta" className="btn-primary hidden md:inline-flex">Get this on your floor <ArrowRight size={14} /></Link>
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
        {/* Hero (active) feed */}
        <div className="lg:col-span-2" data-testid="lv-hero">
          <FeedCard demo={active} big autoplay />
        </div>
        {/* Sidebar of the other feeds — clickable to promote */}
        <div className="grid grid-cols-1 gap-4">
          {demos.map((d, i) => (
            <button
              key={d.id}
              onClick={() => setActiveIdx(i)}
              data-testid={`lv-thumb-${i}`}
              className={`text-left transition-all ${i === activeIdx ? "border border-[#DFFF00]" : "border border-[#2A2A2A] hover:border-[#DFFF00]/50"}`}
            >
              <FeedCard demo={d} big={false} autoplay={false} muted />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders one "camera feed" — a CF Stream player with the DetectionOverlay
 * layered on top.  `big` controls the caption density; `autoplay` starts
 * playback for the hero card only, so scrolled-out clips don't blow bandwidth.
 */
function FeedCard({ demo, big, autoplay, muted }) {
  if (!demo) return null;
  return (
    <div className="relative bg-[#050505] border border-[#2A2A2A] overflow-hidden">
      <div className="relative">
        {/\.m3u8|cloudflarestream\.com/.test(demo.video_url) ? (
          <StreamHLSPlayer src={demo.video_url} autoplay={autoplay} />
        ) : (
          <video src={demo.video_url} autoPlay={autoplay} muted={muted} loop playsInline className="w-full block" />
        )}
        <DetectionOverlay boxes={demo.boxes || []} />
        {/* Corner readouts to sell the "live" feel */}
        <div className="absolute top-3 left-3 flex items-center gap-2 pointer-events-none">
          <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-white bg-[#050505]/70 border border-[#2A2A2A] px-2 py-1">
            <span className="text-[#DFFF00]">●</span> LIVE · {demo.location}
          </div>
        </div>
        <div className="absolute top-3 right-3 pointer-events-none">
          <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#DFFF00] bg-[#050505]/70 border border-[#DFFF00]/40 px-2 py-1">
            ◢ AI on
          </div>
        </div>
      </div>
      <div className={`p-4 border-t border-[#2A2A2A] ${big ? "" : "p-3"}`}>
        <div className={`font-mono ${big ? "text-[10px]" : "text-[9px]"} tracking-[0.22em] uppercase text-[#FF4500]`}>◢ {demo.scenario}</div>
        <div className={`mt-1 font-display text-white ${big ? "text-2xl" : "text-base"} leading-tight`}>{demo.title}</div>
      </div>
    </div>
  );
}

/**
 * DetectionOverlay renders coloured bounding boxes on top of a video.
 * It watches its own `mousemove`? No — it watches the sibling <video> in the
 * DOM using an intersection lookup: for every rAF tick, we take the closest
 * <video> that's inside our parent tree and read `currentTime`.  Boxes with
 * `t` inside the current [t - 0.4, t + 0.4]s window are drawn.
 *
 * Coordinates are 0..1 fractions of the video's rendered rectangle — so we
 * simply position boxes at `left: x*100%, top: y*100%, ...` inside the same
 * container as the video.  Works for both landscape and portrait because
 * Stream's responsive layout resizes the underlying frame naturally.
 */
function DetectionOverlay({ boxes }) {
  const [visibleBoxes, setVisibleBoxes] = useState([]);
  const rafRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!boxes?.length) return;
    const tick = () => {
      const container = containerRef.current;
      const vid = container?.parentElement?.querySelector("video");
      const t = vid?.currentTime || 0;
      const active = boxes.filter((b) => Math.abs(b.t - t) < 0.4);
      setVisibleBoxes(active);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [boxes]);

  const colorFor = (b) => b.color || labelColor(b.label);

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none" data-testid="lv-detection-overlay">
      {visibleBoxes.map((b, i) => (
        <div
          key={i}
          className="absolute border-2 animate-[ww-pop_120ms_ease-out]"
          style={{
            left: `${b.x * 100}%`,
            top: `${b.y * 100}%`,
            width: `${b.w * 100}%`,
            height: `${b.h * 100}%`,
            borderColor: colorFor(b),
            boxShadow: `0 0 12px ${colorFor(b)}55`,
          }}
        >
          <div
            className="absolute -top-6 left-0 font-mono text-[10px] tracking-[0.16em] uppercase px-1.5 py-0.5"
            style={{ background: colorFor(b), color: "#050505" }}
          >
            {b.label} · {Math.round((b.confidence ?? 0.85) * 100)}%
          </div>
        </div>
      ))}
    </div>
  );
}

const LABEL_PALETTE = {
  defect: "#FF4500",
  scratch: "#FF4500",
  dent: "#FF6A1A",
  no_helmet: "#FF3060",
  ppe: "#FF3060",
  person: "#DFFF00",
  forklift: "#00E5FF",
  weld: "#FFAA00",
};
function labelColor(label) {
  return LABEL_PALETTE[(label || "").toLowerCase()] || "#DFFF00";
}
